import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { logger } from '@/utils/logger/logger';

/**
 * Desktop value for the shared `native_platform_source` signal.
 * Matches Mobile's `<Platform>App` naming (`iOSNativeApp` / `AndroidNativeApp`).
 * Confirmed with the iOS lead — no iOS condition depends on this value.
 */
export const DESKTOP_NATIVE_PLATFORM_SOURCE = 'DesktopApp';

/**
 * Shared Mobile+Desktop RC custom-signal keys (user-scoped).
 * Names match Mobile's registered Firebase Analytics user properties so one
 * console condition can OR a User property rule (Mobile) with a Custom signal
 * rule (Desktop).
 */
export const SHARED_REMOTE_CONFIG_SIGNAL_KEYS = [
  'kinde_user_id',
  'native_platform_source',
  'os_version',
  'region',
  'user_ref_id',
  'hardware_serial_number',
  'hardware_fw_version',
] as const;

/** RC custom-signal keys used for Desktop targeting conditions. */
export type RemoteConfigSignalKey = (typeof SHARED_REMOTE_CONFIG_SIGNAL_KEYS)[number];

/** String signals, or null to clear a previously set key. */
export type RemoteConfigSignalValue = string | null;

export type RemoteConfigCustomSignals = Partial<
  Record<RemoteConfigSignalKey, RemoteConfigSignalValue>
>;

const ALL_SIGNAL_KEYS: readonly RemoteConfigSignalKey[] =
  SHARED_REMOTE_CONFIG_SIGNAL_KEYS;

/** Pending signal bag (null entries mean "clear on next flush"). */
let pendingSignals: Record<string, string | null> = {};
/**
 * Fingerprint of the bag the RC SDK currently holds.
 * Cleared to `''` before each `setCustomSignals` so a concurrent restore of a
 * previously-applied bag cannot early-return while a clear write is still in
 * flight (which would leave the fingerprint claiming a bag the SDK no longer
 * holds once the clear lands).
 */
let appliedFingerprint = '';
/** Bumped when pending signals change so caller-owned RC caches re-fetch. */
let signalEpoch = 0;
let flushInFlight: Promise<void> | null = null;

export function getRemoteConfigSignalEpoch(): number {
  return signalEpoch;
}

/**
 * Bare OS version for the shared `os_version` signal — matches iOS
 * `UIDevice.current.systemVersion` (e.g. `"18.2"`, `"14.5"`, `"10.0.22631"`).
 * Strips a leading OS-name prefix if one is present.
 */
export function formatRemoteConfigOsVersion(
  raw: string | null | undefined
): string | null {
  if (raw == null || raw === '') return null;
  const stripped = raw.replace(/^(macOS|Mac OS X?|Windows|iOS|Android)\s+/i, '').trim();
  return stripped === '' ? null : stripped;
}

/**
 * Region for the shared `region` signal — Mobile format:
 * uppercase `AU` / `UK` / `US` / `CA` / `EU`, or lowercase `"unknown"` when absent.
 */
export function formatRemoteConfigRegion(raw: string | null | undefined): string {
  if (raw == null || raw === '') return 'unknown';
  return String(raw).toUpperCase();
}

function fingerprint(signals: Record<string, string | null>): string {
  return JSON.stringify(signals);
}

/**
 * Merges signal updates into the pending bag and schedules a lazy SDK flush.
 * Unchanged keys are no-ops (no epoch bump). Never accepts email.
 */
export function setRemoteConfigCustomSignals(signals: RemoteConfigCustomSignals): void {
  // TELEMETRY-GATE: short-circuit before any Firebase chunk load.
  if (!isSaasTelemetryEnabled) return;
  if (typeof window === 'undefined') return;

  let changed = false;
  for (const [key, value] of Object.entries(signals)) {
    if (value === undefined) continue;
    if (!(ALL_SIGNAL_KEYS as readonly string[]).includes(key)) continue;
    // Empty string is treated as clear — signals are non-empty strings only.
    const normalized = value === '' ? null : value;
    if (pendingSignals[key] !== normalized) {
      pendingSignals[key] = normalized;
      changed = true;
    }
  }
  if (!changed) return;

  signalEpoch += 1;
  void flushRemoteConfigCustomSignals();
}

/** Clears every custom signal on logout so the next session starts clean. */
export function clearRemoteConfigCustomSignals(): void {
  const cleared: RemoteConfigCustomSignals = {};
  for (const key of ALL_SIGNAL_KEYS) {
    cleared[key] = null;
  }
  setRemoteConfigCustomSignals(cleared);
}

/** Clears hardware signals when the remote is unpaired or unknown. */
export function clearRemoteConfigHardwareSignals(): void {
  setRemoteConfigCustomSignals({
    hardware_serial_number: null,
    hardware_fw_version: null,
  });
}

/**
 * Lazy-loads Remote Config and writes pending custom signals.
 * Call before `fetchAndActivate` so the next server fetch sees them.
 *
 * Loops until the SDK holds the latest pending bag. A discarded stale write
 * must not unblock a caller that only awaited that write, and a clear that
 * lands after a same-bag restore must not leave `appliedFingerprint` claiming
 * a bag the SDK no longer holds.
 */
export async function flushRemoteConfigCustomSignals(): Promise<void> {
  // TELEMETRY-GATE: short-circuit before any Firebase chunk load.
  if (!isSaasTelemetryEnabled || typeof window === 'undefined') return;

  try {
    while (fingerprint(pendingSignals) !== appliedFingerprint) {
      if (flushInFlight) {
        try {
          await flushInFlight;
        } catch {
          // Writer already logged; stop so a rejected flush cannot restart
          // forever when the always-stamp is missing.
          return;
        }
        // Re-check the while condition — pending may now match applied, or a
        // newer bag may still need writing after the joined flush.
        continue;
      }

      // Snapshot + epoch before the await so a newer set mid-import skips this
      // stale snapshot rather than writing it.
      const writeEpoch = signalEpoch;
      const writeSnapshot = { ...pendingSignals };
      const writeKey = fingerprint(writeSnapshot);

      let writeFailed = false;
      flushInFlight = (async () => {
        try {
          const [{ setCustomSignals }, { getFirebaseApp }, { getRemoteConfigInstance }] =
            await Promise.all([
              import('firebase/remote-config'),
              import('./firebase'),
              import('./remote-config-instance'),
            ]);
          // Yield so a set() that raced the (sometimes-sync) import is visible
          // to the epoch check before we write a superseded snapshot.
          await Promise.resolve();

          // A newer set/clear won while the chunk was loading — discard this
          // snapshot so we never push a superseded bag into the SDK.
          if (writeEpoch !== signalEpoch) return;

          // Drop the claim before the write so a concurrent restore of a
          // previously-applied bag cannot early-return while this write is
          // still in flight.
          appliedFingerprint = '';
          const remoteConfig = getRemoteConfigInstance(getFirebaseApp());
          await setCustomSignals(remoteConfig, writeSnapshot);

          // The SDK now holds writeSnapshot regardless of epoch races that
          // happened during the await — stamp what was written so the loop
          // can decide whether another write is still needed.
          appliedFingerprint = writeKey;
        } catch (error) {
          writeFailed = true;
          logger.warn({
            msg: '[RemoteConfig] Failed to set custom signals',
            errorName: error instanceof Error ? error.name : 'unknown',
          });
          // Re-throw so joiners awaiting flushInFlight also stop — a swallowed
          // rejection left concurrent flushers restarting forever when the
          // always-stamp was missing (microtask starvation / OOM).
          throw error;
        } finally {
          flushInFlight = null;
        }
      })();

      try {
        await flushInFlight;
      } catch {
        // Logged inside the writer; stop this flusher.
        return;
      }
      // Stop retrying on SDK/chunk failure so a persistent error cannot spin.
      if (writeFailed) return;
    }
  } catch (error) {
    logger.warn({
      msg: '[RemoteConfig] Failed to set custom signals',
      errorName: error instanceof Error ? error.name : 'unknown',
    });
  }
}

/** Test-only: reset module state between suites. */
export function resetRemoteConfigCustomSignalsForTests(): void {
  pendingSignals = {};
  appliedFingerprint = '';
  signalEpoch = 0;
  flushInFlight = null;
}
